import React, { useState, useEffect } from "react";
import BoxeSettings from "./BoxeSettings";
import BoxeMatch from "./BoxeMatch";
import ResultatBoxe from "./ResultatBoxe";
import AccueilBoxe from "./AccueilBoxe";
import BoxedeuxBoxeurs from "./BoxedeuxBoxeurs";
import SoloStart from "./SoloStart";
import ResultatSoloBoxe from "./ResultatSoloBoxe";
import BoxeUnBoxeur from "./BoxeUnBoxeur";
import api from "../../api";
import jugeboxeSessionKeys from "./jugeboxeSessionKeys";

/**
 * JugeBoxeApp (updated)
 *
 * - When user chooses 1 (classic) or 'solo' (quick) or nbBoxeurs === 1 from settings,
 *   route to the appropriate component:
 *     * 'solo' -> SoloStart (quick flow)
 *     * nbBoxeurs === 1 -> BoxeUnBoxeur (full single-boxer flow)
 * - Keeps 2-boxeurs flow unchanged (BoxedeuxBoxeurs).
 */

function QRCodePage({ onRetour }) {
  return (
    <div style={{
      minHeight: "100vh",
      background: "#000",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center"
    }}>
      <h3 style={{ color: "#fff", marginBottom: 16 }}>Scannez avec votre smartphone</h3>
      <img
        src="/assets/qrcode.jpeg"
        alt="QR code JUGE BOXE"
        style={{
          width: 220,
          height: 220,
          background: "#fff",
          borderRadius: 8,
          marginBottom: 32
        }}
      />
      <button
        style={{
          background: "#a51c30", color: "#fff", border: "none", borderRadius: 8,
          padding: "10px 26px", fontSize: 18, cursor: "pointer"
        }}
        onClick={onRetour}
      >Retour</button>
    </div>
  );
}

export default function JugeBoxeApp({ user }) {
  // step: "accueil" | "qr" | "reglages" | "match" | "resultat" | "solo-start" | "resultat-solo"
  const [step, setStep] = useState("accueil");
  const [qrMode, setQrMode] = useState(false);
  const [params, setParams] = useState(null); // { nbBoxeurs, type, durationMin, durationSec, ... }
  const [result, setResult] = useState(null);
  const [qrExchangeLoading, setQrExchangeLoading] = useState(false);
  const [qrExchangeError, setQrExchangeError] = useState("");
  // classeId pre-set from student QR session
  const [studentClasseId, setStudentClasseId] = useState(
    () => sessionStorage.getItem(jugeboxeSessionKeys.classeId) || null
  );

  // Mode QR via URL paramètre (plein écran direct)
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);

    // Student QR token exchange: ?t=<rawToken>
    const rawToken = q.get("t");
    if (rawToken) {
      setQrExchangeLoading(true);
      setQrExchangeError("");
      api.post('/jugeboxe/qr/exchange', { token: rawToken })
        .then(res => {
          const { sessionToken, classeId } = res.data;
          sessionStorage.setItem(jugeboxeSessionKeys.studentToken, sessionToken);
          sessionStorage.setItem(jugeboxeSessionKeys.classeId, classeId);
          sessionStorage.setItem(jugeboxeSessionKeys.authFlag, '1');
          setStudentClasseId(classeId);
          q.delete("t");
          const query = q.toString();
          const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
          window.history.replaceState({}, document.title, nextUrl);
          // Stay on accueil so student can choose 1 or 2 boxers
        })
        .catch(err => {
          console.error('JugeBoxeApp: QR exchange failed', err);
          setQrExchangeError(err?.response?.data?.error || "Impossible d'ouvrir la session élève via le QR code.");
        })
        .finally(() => {
          setQrExchangeLoading(false);
        });
      return;
    }

    if (q.get("qr") === "1") {
      setStep("reglages");
      setQrMode(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build params for boxer components, merging student classeId if available
  function buildBoxerParams(mode) {
    if (studentClasseId) {
      return {
        nbBoxeurs: mode,
        classesList: [{ _id: studentClasseId, nom: 'Classe liée au QR' }],
        initialClasseId: studentClasseId,
        isStudentSession: true,
        getElevesFromClasse: async (classeId) => {
          const res = await api.get(`/jugeboxe/eleves?classe=${encodeURIComponent(classeId || studentClasseId)}`, {
            useStudentAuth: true,
          });
          return Array.isArray(res.data) ? res.data : [];
        },
      };
    }
    return { nbBoxeurs: mode };
  }

  if (qrExchangeLoading) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        background: "linear-gradient(120deg, #1c1c2b 50%, #a51c30 100%)",
        color: "#fff",
        textAlign: "center",
        padding: 24,
      }}>
        <h2>Connexion élève en cours…</h2>
        <p>Le QR code est en train d’ouvrir ta session Boxe.</p>
      </div>
    );
  }

  if (qrExchangeError) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        background: "linear-gradient(120deg, #1c1c2b 50%, #a51c30 100%)",
        color: "#fff",
        textAlign: "center",
        padding: 24,
      }}>
        <h2>Accès QR impossible</h2>
        <p>{qrExchangeError}</p>
      </div>
    );
  }

  // Accueil stylée
  if (step === "accueil") {
    return (
      <AccueilBoxe user={user} isStudentSession={Boolean(studentClasseId)} onSelectMode={mode => {
        if (mode === "qr") {
          setStep("qr");
          return;
        }
        if (mode === 'solo') {
          setParams({ nbBoxeurs: 1, solo: true });
          setStep("solo-start");
          return;
        }
        // numeric modes (1 or 2)
        setParams(buildBoxerParams(mode));
        setStep("reglages");
      }} />
    );
  }

  // Page QR code statique
  if (step === "qr") {
    return (
      <QRCodePage onRetour={() => setStep("accueil")} />
    );
  }

  // Réglages du match
  if (step === "reglages") {
    // If it's a 2-boxeurs flow, use the combined BoxedeuxBoxeurs component
    if (params?.nbBoxeurs === 2) {
      return (
        <BoxedeuxBoxeurs
          classesList={params?.classesList}
          initialClasseId={params?.initialClasseId}
          getElevesFromClasse={params?.getElevesFromClasse}
          isStudentSession={params?.isStudentSession}
          onFinish={res => { setResult(res); setStep("resultat"); }}
          onBack={() => { setStep("accueil"); setParams(null); }}
        />
      );
    }

    // If nbBoxeurs === 1, open full single-boxer component
    if (params?.nbBoxeurs === 1) {
      return (
        <BoxeUnBoxeur
          classesList={params?.classesList}
          initialClasseId={params?.initialClasseId}
          getElevesFromClasse={params?.getElevesFromClasse}
          isStudentSession={params?.isStudentSession}
          onFinish={res => { setResult(res); setStep("resultat"); }}
          onBack={() => { setStep("accueil"); setParams(null); }}
        />
      );
    }

    // fallback to BoxeSettings if no params
    return (
      <BoxeSettings
        nbBoxeurs={params?.nbBoxeurs}
        onValider={p => { setParams({ ...params, ...p }); setStep("match"); }}
        onRetour={() => { setStep("accueil"); setParams(null); }}
        qrMode={qrMode}
      />
    );
  }

  // Solo start: selection élève + points -> ouvre ResultatSoloBoxe via state
  if (step === "solo-start") {
    return (
      <SoloStart />
    );
  }

  // Match (only reached for the separated flow) - kept for backward compatibility
  if (step === "match") {
    return (
      <BoxeMatch
        params={params}
        onFinish={res => { setResult(res); setStep("resultat"); }}
        onHome={() => { setStep(qrMode ? "reglages" : "accueil"); setParams(null); }}
        onReset={() => setStep("reglages")}
        qrMode={qrMode}
      />
    );
  }

  // Résultat (both flows use same result screen)
  if (step === "resultat") {
    // ResultatBoxe handles dual result; ResultatSoloBoxe handles solo — decide based on result.params.nbBoxeurs or participants
    const isSolo = result?.params?.nbBoxeurs === 1 || result?.participants?.boxerId || result?.participants?.nomBoxer;
    if (isSolo) {
      return <ResultatSoloBoxe result={result} onRestart={() => { setStep("reglages"); setResult(null); }} onHome={() => { setStep(qrMode ? "reglages" : "accueil"); setParams(null); setResult(null); }} qrMode={qrMode} />;
    }
    return (
      <ResultatBoxe
        result={result}
        onRestart={() => { setStep("reglages"); setResult(null); }}
        onHome={() => { setStep(qrMode ? "reglages" : "accueil"); setParams(null); setResult(null); }}
        qrMode={qrMode}
      />
    );
  }

  // Résultat solo explicit (kept for direct navigation)
  if (step === "resultat-solo") {
    return (
      <ResultatSoloBoxe
        result={result}
        onRestart={() => { setStep("solo-start"); setResult(null); }}
        onHome={() => { setStep(qrMode ? "reglages" : "accueil"); setParams(null); setResult(null); }}
        qrMode={qrMode}
      />
    );
  }

  return null;
}